import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { ChatBubbleOutline as ChatBubbleIcon, Delete as DeleteIcon, Edit as EditIcon, Receipt as ReceiptIcon } from '@mui/icons-material';
import { TimeDisplay } from '../common';
import CouponValidityPeriod from './CouponValidityPeriod';
import { Coupon } from '../../types/coupon';

interface CouponTableProps {
  coupons: Coupon[];
  totalCount: number;
  page: number;
  rowsPerPage: number;
  loading: boolean;
  onPageChange: (event: unknown, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRowClick?: (coupon: Coupon) => void;
  onEditClick?: (coupon: Coupon) => void;
  onDeleteClick?: (coupon: Coupon) => void;
}

const CouponTable: React.FC<CouponTableProps> = ({
  coupons,
  totalCount,
  page,
  rowsPerPage,
  loading,
  onPageChange,
  onRowsPerPageChange,
  onRowClick,
  onEditClick,
  onDeleteClick,
}) => {
  const navigate = useNavigate();
  const getStatusChip = (coupon: Coupon) => {
    const chipStyle = {
      fontWeight: 'bold',
      minWidth: '80px',
      maxWidth: '100%'
    };
    let chipElement;
    if (coupon.disabled) {
      chipElement = <Chip label="無効" size="small" color="error" sx={chipStyle}/>;
    } else if (coupon.is_active) {
      chipElement = <Chip label="期間中" size="small" color="success" sx={chipStyle}/>;
    } else {
      const today = new Date();
      const startDate = new Date(coupon.start_date);
      if (today < startDate) {
        chipElement = <Chip label="開始前" size="small" color="warning" sx={chipStyle}/>;
      } else if (coupon.end_date) {
        const endDate = new Date(coupon.end_date);
        if (today > endDate) {
          chipElement = <Chip label="終了" size="small" color="default" sx={chipStyle}/>;
        } else {
          chipElement = <Chip label="不明" size="small" sx={chipStyle}/>;
        }
      } else {
        chipElement = <Chip label="不明" size="small" sx={chipStyle}/>;
      }
    }
    return (
      <Tooltip title={`ID: ${coupon.id}`} arrow placement="top">
        {chipElement}
      </Tooltip>
    );
  };
  return (
    <Paper elevation={2}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="center">ステータス</TableCell>
              <TableCell sx={{ width: '25%' }}>クーポン (コード)</TableCell>
              <TableCell>割引</TableCell>
              <TableCell>有効期間</TableCell>
              <TableCell align="center">利用状況</TableCell>
              <TableCell>備考</TableCell>
              <TableCell>作成日</TableCell>
              {onEditClick && <TableCell>操作</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={onEditClick ? 8 : 7} align="center">
                  <Typography>読み込み中...</Typography>
                </TableCell>
              </TableRow>
            ) : coupons.length === 0 ? (
              <TableRow>
                <TableCell colSpan={onEditClick ? 8 : 7} align="center">
                  <Typography>該当するクーポンがありません</Typography>
                </TableCell>
              </TableRow>
            ) : (
              coupons.map((coupon) => {
                const isFullyUsed = coupon.is_active && coupon.usage_limit && coupon.usages_count >= coupon.usage_limit;
                const isDisabled = coupon.disabled;
                return (
                  <TableRow
                    key={coupon.id}
                    sx={{
                      cursor: onRowClick ? 'pointer' : 'default',
                      backgroundColor: isDisabled ? 'rgba(0, 0, 0, 0.05)' : isFullyUsed ? 'rgba(255, 192, 203, 0.2)' : 'inherit',
                    }}
                    onClick={() => onRowClick?.(coupon)}
                  >
                    <TableCell align="center">
                      {getStatusChip(coupon)}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {coupon.name ? (
                          <>
                            {coupon.name} (<span style={{ fontWeight: 'bold', fontSize: '1.1em' }}>{coupon.code}</span>)
                          </>
                        ) : (
                          <span style={{ fontWeight: 'bold', fontSize: '1.1em' }}>{coupon.code}</span>
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {coupon.discount_type === 'FIXED' ? (
                          <>
                            {coupon.discount_value.toLocaleString()} <span style={{ fontWeight: 'bold' }}>円</span>
                          </>
                        ) : coupon.discount_type === 'RATE' ? (
                          <>
                            {coupon.discount_value} <span style={{ fontWeight: 'bold' }}>%</span>
                          </>
                        ) : (
                          coupon.discount_display
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <CouponValidityPeriod coupon={coupon}/>
                    </TableCell>
                    <TableCell align="center">
                      {coupon.usages_count > 0 ? (
                        <Link
                          to={`/coupons/${coupon.id}/usages`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          <Box
                            sx={{
                              cursor: 'pointer',
                              color: 'primary.main',
                              '&:hover': { textDecoration: 'underline' },
                            }}
                          >
                            <Typography variant="body2" sx={{ fontSize: isFullyUsed ? '1.3rem' : '1rem' }}>
                              {coupon.usage_limit ? (
                                isFullyUsed ? (
                                  <>
                                  <span style={{
                                    fontWeight: 'bold',
                                    color: '#d32f2f'
                                  }}>{coupon.usages_count}</span>
                                    <span style={{ fontSize: '0.85em' }}>
                                    {' / '}
                                      {coupon.usage_limit}回
                                  </span>
                                  </>
                                ) : (
                                  <>
                                    <span style={{ fontWeight: 'bold' }}>{coupon.usages_count}</span>
                                    {' / '}
                                    {coupon.usage_limit}回
                                  </>
                                )
                              ) : (
                                <>
                                  <span style={{ fontWeight: 'bold' }}>{coupon.usages_count}</span>
                                  回
                                </>
                              )}
                              <ReceiptIcon
                                sx={{
                                  fontSize: '1.2rem',
                                  verticalAlign: 'middle',
                                  ml: 0.5,
                                  display: 'inline'
                                }}
                              />
                            </Typography>
                          </Box>
                        </Link>
                      ) : (
                        <Typography variant="body2" sx={{ fontSize: isFullyUsed ? '1.3rem' : '1rem' }}>
                          {coupon.usage_limit ? (
                            <>
                              <span style={{ fontWeight: 'bold' }}>{coupon.usages_count}</span>
                              {' / '}
                              {coupon.usage_limit}回
                            </>
                          ) : (
                            <>
                              <span style={{ fontWeight: 'bold' }}>{coupon.usages_count}</span>
                              回
                            </>
                          )}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {coupon.remarks ? (
                        coupon.remarks.length > 20 ? (
                          <Tooltip
                            title={coupon.remarks}
                            arrow
                            placement="top"
                            enterDelay={500}
                            sx={{ maxWidth: 500 }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'help'
                              }}
                            >
                              {coupon.remarks.substring(0, 20)}...
                              <ChatBubbleIcon sx={{ ml: 0.5, fontSize: '0.9rem', color: 'text.secondary', opacity: 0.7 }}/>
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2">
                            {coupon.remarks}
                          </Typography>
                        )
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <TimeDisplay
                        timestamp={coupon.created_at}
                        tooltipPrefix="作成日時"
                        fontSize="0.875rem"
                      />
                    </TableCell>
                    {onEditClick && (
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <IconButton
                            size="medium"
                            onClick={(e) => {
                              e.stopPropagation();
                              onEditClick(coupon);
                            }}
                            sx={{
                              backgroundColor: 'rgba(25, 118, 210, 0.08)',
                              color: 'primary.main',
                              '&:hover': {
                                backgroundColor: 'rgba(25, 118, 210, 0.12)',
                                color: 'primary.main',
                              }
                            }}
                          >
                            <EditIcon/>
                          </IconButton>
                          {onDeleteClick && coupon.can_delete && (
                            <IconButton
                              size="medium"
                              onClick={(e) => {
                                e.stopPropagation();
                                onDeleteClick(coupon);
                              }}
                              sx={{
                                backgroundColor: 'rgba(255, 0, 0, 0.08)',
                                color: 'error.main',
                                '&:hover': {
                                  backgroundColor: 'rgba(255, 0, 0, 0.12)',
                                  color: 'error.dark',
                                }
                              }}
                            >
                              <DeleteIcon/>
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[ 10, 20, 50 ]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        labelRowsPerPage="表示件数:"
        labelDisplayedRows={({ from, to, count }) =>
          `${count}件中 ${from}-${to}件`
        }
      />
    </Paper>
  );
};
export default CouponTable;
